import type { PortableTextBlock } from '@portabletext/types';
import type { FaqListItem } from '@time-line/components/common';
import type { CloudinaryVideo, SanityImage } from '@time-line/components/types';
import type { Seo } from 'services/shopify';
import type { SiteCategory } from 'types/common/SiteCategory';
import type { ProductType } from 'types/product/Product';

import type {
  Banner,
  BeforeAfter,
  Benefits,
  CategoriesIntro,
  Certifications,
  ChartBlock,
  ComparisonTable,
  FeatureList,
  HaloShowcase,
  IconsList,
  ImageWithCallouts,
  MediaBlock,
  MitopureSolution,
  PressQuotes,
  ProductComparison,
  ReviewsCarousel,
  SocialMediaCarousel,
  TextBlock,
  TextMediaSplit,
  VibeGallery,
} from '../blocks';
import type { ProductBenefits } from '../ProductBenefits';
import type { ProductSlug } from '../ProductSlug';
import type { SocialMediaStory } from '../SocialMediaStory';
import type { Ingredient } from './IngredientsPage';

export interface ProductHowToUse {
  contentType: 'none' | 'description' | 'steps';
  description: PortableTextBlock[];
  steps: {
    title: string;
    description?: string;
    image?: SanityImage;
  }[];
}

export type ProductMedia = SanityImage | CloudinaryVideo;

export const isSanityImage = (m: ProductMedia): m is SanityImage =>
  m._type === 'image' || m._type === 'altImage';

export type Ingredients = {
  activeIngredients?: Ingredient[];
  mitopureIngredient?: Ingredient;
  productFacts?: string[];
};

type Blocks = Array<
  | MitopureSolution
  | TextMediaSplit
  | Certifications
  | IconsList
  | BeforeAfter
  | ImageWithCallouts
  | CategoriesIntro
  | ChartBlock
  | Banner
  | ComparisonTable
  | ReviewsCarousel
  | MediaBlock
  | TextBlock
  | ProductComparison
  | FeatureList
  | Benefits
  | SocialMediaCarousel
  | VibeGallery
  | HaloShowcase
>;

export interface ComplementaryProducts {
  title?: string;
  products: {
    slug: string;
    href: string;
    image: SanityImage;
    variant: { gid: string };
  }[];
}

export interface ProductPage {
  category: SiteCategory;
  media: ProductMedia[];
  mostPopularVariantId?: string;
  bestValueVariantId?: string;
  longDescription: PortableTextBlock[];
  productType: ProductType;
  frontrowMD?: {
    productId?: string;
    isFrontrowBadgeEnabled: boolean;
    isFrontrowTestimonialsEnabled: boolean;
    isFrontrowClinicianAIEnabled: boolean;
  };
  relatedProducts?: {
    title: string;
    products: ProductSlug[];
    plp?: {
      label?: string;
      ctaLabel?: string;
      slug: string;
      href: string;
    };
  };
  productFormats?: Array<{
    product: { slug: string; formatLabel: string };
    variant: { gid: string };
  }>;
  alternativeProduct?: {
    product: {
      title: string;
      subtitle: string;
      label: string;
      slug: string;
      href: string;
      image: SanityImage;
      variant: { gid: string };
    };
  };
  complementaryProducts?: ComplementaryProducts;
  bulletPoints?: PortableTextBlock[];
  disclaimer?: string;
  howToUseV2?: ProductHowToUse;
  benefits?: ProductBenefits;
  ingredients?: Ingredients;
  informationPanel?: {
    alt: string;
    url: string;
  };
  blocks: Blocks;
  bundleProducts: {
    product: ProductSlug;
    isFree: boolean;
    variant?: { gid: string };
  }[];
  seo?: Seo;
  faq: {
    title: string;
    subtitle?: PortableTextBlock[];
    isFAQLinkEnabled?: boolean;
    list: FaqListItem[];
  };
  pressQuotes: PressQuotes;
  seeItInAction?: {
    title: string;
    items: SocialMediaStory[];
  };
}
